import { BellOffIcon } from "lucide-react"
import { cn } from "@/primitives/utils"
import { unreadCountShown } from "./unread.ts"

/**
 * That one thing is unread, as a small filled dot at a row's end; `said` is what a screen reader
 * reads in its place, after the row's name ("…, unread").
 */
export function UnreadDot({ said }: { said: string }) {
  return (
    <span className="flex size-5 shrink-0 items-center justify-center">
      <span aria-hidden className="size-2 rounded-full bg-foreground" />
      <span className="sr-only">, {said}</span>
    </span>
  )
}

/**
 * How many are unread, as a small filled count at a row's end; nothing when none. The figure is
 * drawn for the eye; `said` is what a screen reader reads in its place, after the row's name
 * ("general, 3 unread messages"). `quiet` draws it outlined and muted, for a conversation the
 * Member muted: still counted, never calling for them.
 */
export function UnreadCount({
  count,
  said,
  quiet = false,
}: {
  count: number
  said: string
  quiet?: boolean
}) {
  if (count <= 0) return null
  return (
    <span
      data-quiet={quiet || undefined}
      className={cn(
        "flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-2xs font-semibold tabular-nums",
        quiet
          ? "text-subtle-foreground ring-1 ring-border ring-inset"
          : "bg-foreground text-background",
      )}
    >
      <span aria-hidden>{unreadCountShown(count)}</span>
      <span className="sr-only">, {said}</span>
    </span>
  )
}

/** That the Member muted a conversation, as a small struck bell after its name. */
export function MutedMark() {
  return (
    <>
      <BellOffIcon aria-hidden className="size-3.5 shrink-0 text-subtle-foreground" />
      <span className="sr-only">, muted</span>
    </>
  )
}
